import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { countrySchema, CountryFormData } from '../schemas/master-data.schemas';
import { Country } from '../types/master-data.types';
import { Loader2 } from 'lucide-react';

interface CountryFormProps {
  country?: Country;
  onSubmit: (data: CountryFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CountryForm({ country, onSubmit, onCancel, isLoading }: CountryFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CountryFormData>({
    resolver: zodResolver(countrySchema),
    defaultValues: country
      ? { name: country.name, code: country.code, sapCode: country.sapCode ?? undefined }
      : { name: '', code: '', sapCode: undefined },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="code">Código ISO *</Label>
          <Input
            id="code"
            placeholder="BO"
            maxLength={2}
            {...register('code')}
            error={errors.code?.message}
            disabled={isLoading || !!country}
            className="uppercase"
          />
          <p className="text-xs text-muted-foreground">Código de 2 letras (ISO 3166-1)</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            placeholder="Bolivia"
            {...register('name')}
            error={errors.name?.message}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sapCode">Código SAP</Label>
        <Input
          id="sapCode"
          placeholder="Código equivalente en SAP (opcional)"
          maxLength={64}
          {...register('sapCode')}
          error={(errors as any).sapCode?.message}
          disabled={isLoading}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : country ? (
            'Actualizar'
          ) : (
            'Crear'
          )}
        </Button>
      </div>
    </form>
  );
}

export default CountryForm;
